import type { AiPrintoutData } from "./contract";

/** One category value and how many shifts on the day carry it. */
export type DesignContextCount<TValue> = TValue & { count: number };

/**
 * What the printout designer is told about a department's day: real categories and counts so a layout
 * fits the page — and never a person, and never money. No names, initials, nurse ids, notes, time-off
 * reasons or call-out text reach it; only the longest name's length, so columns can be sized. Of the
 * metrics it learns only which ones the day has, never a cost or PPD value.
 */
export type AiPrintoutDesignContext = {
	contractVersion: number;
	isActual: boolean;
	usesJobs: boolean;
	nameOrder: AiPrintoutData["department"]["nameOrder"];
	dir: AiPrintoutData["dir"];
	shiftCount: number;
	gapCount: number;
	ptoCount: number;
	callOutCount: number;
	chargeCount: number;
	agencyCount: number;
	pendingCount: number;
	shiftsWithBreaksCount: number;
	longestNameLength: number;
	/** Shown shifts not onboarded, time off with a reason, and the header admins the viewer lists. */
	notOnboardedCount: number;
	ptoWithReasonCount: number;
	adminCount: number;
	/** Shown shifts with the other side's shift (`scheduled`/`actual` both set), and the other side's leftovers. */
	matchedCount: number;
	unmatchedCount: number;
	requirementCount: number;
	/** Which of the shown side's metrics are non-null today. */
	hasCensus: boolean;
	hasLocationCensus: boolean;
	hasCensusReadings: boolean;
	hasHppdTarget: boolean;
	hasPpd: boolean;
	hasPpdTarget: boolean;
	/** The time groups with how many shown shifts each holds; empty when `data.timeGroups` is null. */
	timeGroups: DesignContextCount<{ label: string; timeLabel: string | null; isOther: boolean }>[];
	roles: DesignContextCount<{ role: string; roleShort: string }>[];
	jobs: DesignContextCount<{ jobName: string }>[];
	locations: DesignContextCount<{ location: string; floor: string | null }>[];
	floors: DesignContextCount<{ floor: string }>[];
	shiftTimes: DesignContextCount<{ timeLabel: string; startMinutesFromDayStart: number }>[];
	employmentTypes: DesignContextCount<{ employmentType: string }>[];
	labels: AiPrintoutData["labels"];
};

/** Counts items by a projected category, keeping the first-seen order. Items projecting to null are skipped. */
const countBy = <TItem, TValue extends object>(items: TItem[], project: (item: TItem) => TValue | null): DesignContextCount<TValue>[] => {
	const counted = new Map<string, DesignContextCount<TValue>>();
	for (const item of items) {
		const value = project(item);
		if (value === null) continue;
		const key = JSON.stringify(value);
		const existing = counted.get(key);
		if (existing) existing.count += 1;
		else counted.set(key, { ...value, count: 1 });
	}
	return [...counted.values()];
};

const timeGroupCounts = (data: AiPrintoutData): AiPrintoutDesignContext["timeGroups"] => {
	if (data.timeGroups === null) return [];
	return data.timeGroups.map(({ key, label, timeLabel, isOther }) => ({
		label,
		timeLabel,
		isOther,
		count: data.shifts.filter((shift) => shift.timeGroupKeys?.includes(key)).length,
	}));
};

/** The day's metric availability, never a value: a cost or PPD dollar amount never reaches the model. */
const metricFlags = (data: AiPrintoutData) => {
	const shown = data.isActual ? data.metrics.actual : data.metrics.scheduled;
	return {
		hasCensus: data.census.total !== null,
		hasLocationCensus: data.census.byLocation !== null,
		hasCensusReadings: data.census.readings !== null,
		hasHppdTarget: shown.hppdTarget !== null,
		hasPpd: shown.ppd !== null,
		hasPpdTarget: shown.ppdTarget !== null,
	};
};

export const buildDesignContext = (data: AiPrintoutData): AiPrintoutDesignContext => {
	const { shifts } = data;
	return {
		contractVersion: data.contractVersion,
		isActual: data.isActual,
		usesJobs: data.department.usesJobs,
		nameOrder: data.department.nameOrder,
		dir: data.dir,
		shiftCount: shifts.length,
		gapCount: data.gaps.length,
		ptoCount: data.pto.length,
		callOutCount: data.callOuts.length,
		chargeCount: shifts.filter((shift) => shift.charge).length,
		agencyCount: shifts.filter((shift) => shift.isAgency).length,
		pendingCount: shifts.filter((shift) => shift.pending !== "none").length,
		shiftsWithBreaksCount: shifts.filter((shift) => shift.breaks.length > 0).length,
		longestNameLength: Math.max(0, ...shifts.map((shift) => shift.name.length)),
		notOnboardedCount: shifts.filter((shift) => shift.notOnboarded).length,
		ptoWithReasonCount: data.pto.filter((entry) => entry.reason !== null && entry.reason !== "").length,
		adminCount: data.admins === null ? 0 : data.admins.length,
		matchedCount: shifts.filter((shift) => shift.scheduled !== null && shift.actual !== null).length,
		unmatchedCount: data.unmatched.length,
		requirementCount: data.requirements.length,
		...metricFlags(data),
		timeGroups: timeGroupCounts(data),
		roles: countBy(shifts, (shift) => ({ role: shift.role, roleShort: shift.roleShort })),
		jobs: countBy(shifts, (shift) => (shift.jobName === null ? null : { jobName: shift.jobName })),
		locations: countBy(shifts, (shift) => (shift.location === null ? null : { location: shift.location, floor: shift.floor })),
		floors: countBy(shifts, (shift) => (shift.floor === null ? null : { floor: shift.floor })),
		shiftTimes: countBy(shifts, (shift) => ({ timeLabel: shift.timeLabel, startMinutesFromDayStart: shift.startMinutesFromDayStart })),
		employmentTypes: countBy(shifts, (shift) => ({ employmentType: shift.employmentType })),
		labels: data.labels,
	};
};
